/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {
  CTCEntity
} from '@ion/core/src/types/entities';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown, {Options} from '@ion/dls/src/components/menus/Dropdown';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptions,
  getOptionSelected,
  onDropDownChange
} from '@ion/features/src/helpers';

// defines the states chooser prop definitions ...
export type StateChooserProps = {
  onChange?: (states: CTCEntity, key: number) => void;
  readOnly?: boolean;
  required?: boolean;
  validationError?: string;
  classes?: string;
  defaultValue?: string;
  states: any;
  keyObj: any;
  focusStateField: boolean;
}

// defines the styles used by states chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
export const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : dlsZIndex('stateChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  formControl : {
    borderBottom           : ({hasError}: {hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: {hasError: boolean;}) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : 'none !important'
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 *
 * The StateChooser provides a list of states for the user's default account
 * for display within a dropdown list and allows the user to search and select an states
 * from the list which will be returned to the client as the selected states.
 *
 *     // read-only mode ...
 *     <StateChooser readOnly
 *       />
 *
 *     // with default value and required check ...
 *     <StateChooser required
 *       />
 *
 *     // with callback subscription ...
 *     <StateChooser onChange={setStates}
 *       />
 *
 * @onChange         Callback prop which will be invoked with the seleted states.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @defaultValue     Default Value of the field
 */
export const StateChooser = ({
  onChange,
  readOnly,
  required,
  classes,
  validationError,
  defaultValue,
  states,
  keyObj,
  focusStateField
}: StateChooserProps) => {
  const hasError                              = Boolean(validationError);
  const _classes                              = useStyles({hasError});
  const [open, setOpen]                       = useState(false);
  const [selected, setSelected]               = useState<string>(defaultValue);
  const [options, setOptions]                 = useState<Options[]>([]);
  const inputRef                              = useRef(null);

  // manages loading states based on current search term ...
  function loadStates() {
    if (typeof states !== 'string') {
      setOptions(getOptions(states));
    }
  }
  useEffect(() => {
    setSelected(defaultValue);
    if (focusStateField) {
      inputRef.current?.focus();
    }
  }, [defaultValue]);

  useEffect(() => {
    loadStates();
  }, [states]);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    if (e) {
      e?.persist();
    }
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Dropdown options={options}
          fieldLabel={translate('contacts:address.stateForm.label')}
          name={translate('contacts:address.stateForm.name')}
          placeholder={translate('contacts:address.stateForm.placeholder')}
          defaultValue={defaultValue || ''}
          required={required}
          hasError={hasError}
          disabled={readOnly}
          onChange={
            function(e, value) {
              const params = {
                setSelected, listObj : states, keyObj, isAddress : true
              };
              return onDropDownChange(onChange, value, params);
            }
          }
          value={selected || ''} />
      </FormControl>
    </div>
  );
};

export default StateChooser;
